import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getNotifications } from '../../services/notificationService';
import Preloader from './Preloader';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  const dropdownRef = React.useRef(null);
  const mobileNotificationRef = React.useRef(null);
  const desktopNotificationRef = React.useRef(null);

  React.useEffect(() => {
    const fetchNotifications = async () => {
      if (user) {
        try {
          const data = await getNotifications();
          setNotifications(data);
        } catch (error) {
          console.error("Failed to fetch notifications", error);
        }
      }
    };
    fetchNotifications();
    // Poll every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  React.useEffect(() => {
    const handleOpenNotifications = () => {
      setIsNotificationOpen(true);
    };
    window.addEventListener('openNotifications', handleOpenNotifications);
    return () => window.removeEventListener('openNotifications', handleOpenNotifications);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      const isOutsideMobile = mobileNotificationRef.current && !mobileNotificationRef.current.contains(event.target);
      const isOutsideDesktop = desktopNotificationRef.current && !desktopNotificationRef.current.contains(event.target);
      
      if (isOutsideMobile && isOutsideDesktop) {
        setIsNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 300);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const getShortName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 0) return '';
    
    const titles = ['mr.', 'ms.', 'mrs.', 'dr.', 'prof.', 'mr', 'ms', 'mrs', 'dr', 'prof'];
    if (titles.includes(parts[0].toLowerCase()) && parts.length > 1) {
      return `${parts[0]} ${parts[1]}`;
    }
    
    return parts[0];
  };

  const formatDateTime = (dateString) => {
    const d = new Date(dateString);
    const DD = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const YYYY = d.getFullYear();
    return `${DD}:${MM}:${YYYY}`;
  };

  const displayPhoto = user?.role === 'admin' ? require('../../assets/finallogo.png') : user?.profilePhoto;

  if (isLoggingOut) {
    return <Preloader />;
  }

  return (
    <div className="layout">
      <nav className="navbar">
        <div className="nav-container">
          {/* ✅ Logo + Text as ONE clickable brand unit */}
          <Link to={user?.role === 'admin' ? '/admin/dashboard' : '/employee/dashboard'} className="nav-brand-container" style={{ textDecoration: 'none' }}>
            <img src={require('../../assets/CRZ_EMS_LOGO.png')} alt="CollegeReviewZ" className="nav-logo" />
          </Link>

          {/* Background Blur Overlay for Notifications */}
          {isNotificationOpen && (
            <div className="notification-backdrop" onClick={() => setIsNotificationOpen(false)}></div>
          )}

          <div className="mobile-header-right">
            {displayPhoto && (
              <img 
                src={displayPhoto} 
                alt="Profile" 
                className={`nav-profile-photo mobile-photo ${user?.role === 'admin' ? 'admin-logo' : ''}`} 
              />
            )}
            <div className="notification-container" ref={mobileNotificationRef}>
              <button 
                className="nav-notification-btn mobile-notification"
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              >
                <Bell size={20} />
                {notifications.length > 0 && <span className="nav-notification-badge">{notifications.length}</span>}
              </button>
              {isNotificationOpen && (
                <div className="notification-dropdown-menu">
                  <div className="notification-header">
                    <h4>Notifications</h4>
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <p className="no-notifications">No new notifications</p>
                    ) : (
                      notifications.map(notif => (
                        <Link to={notif.link} key={notif.id} className="notification-item" onClick={() => setIsNotificationOpen(false)}>
                          {notif.requestType ? (
                            <>
                              <p className="notification-title">{notif.requestType}</p>
                              <p className="notification-name" style={{ margin: '4px 0', fontSize: '14px', fontWeight: 'bold', background: 'linear-gradient(90deg, #003366, #00BFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>{notif.name}</p>
                              <p className="notification-action" style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>{notif.action}</p>
                            </>
                          ) : (
                            <>
                              <p className="notification-title">{notif.title}</p>
                              <p className="notification-message">{notif.message}</p>
                            </>
                          )}
                          <span className="notification-time">{formatDateTime(notif.date)}</span>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          </div>

          <div className={`nav-menu ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            {user && (
              <>
                <div className="nav-links">
                  {user.role === 'admin' && (
                    <>
                      <Link to="/admin/dashboard" className="nav-link" onClick={closeMobileMenu}>
                        Dashboard
                      </Link>
                      <Link to="/admin/employees" className="nav-link" onClick={closeMobileMenu}>
                        Employees
                      </Link>
                      <Link to="/admin/tasks" className="nav-link" onClick={closeMobileMenu}>
                        Tasks
                      </Link>
                      <Link to="/admin/leaves" className="nav-link" onClick={closeMobileMenu}>
                        Leaves
                      </Link>
                      <Link to="/admin/holidays" className="nav-link" onClick={closeMobileMenu}>
                        Holidays
                      </Link>
                      <Link to="/admin/attendance" className="nav-link" onClick={closeMobileMenu}>
                        Attendance
                      </Link>
                      <Link to="/admin/work-updates" className="nav-link" onClick={closeMobileMenu}>
                        Work Updates
                      </Link>
                      <Link to="/admin/notifications" className="nav-link" onClick={closeMobileMenu}>
                        Notifications
                      </Link>
                    </>
                  )}
                  {user.role === 'employee' && (
                    <>
                      <Link to="/employee/dashboard" className="nav-link" onClick={closeMobileMenu}>
                        Dashboard
                      </Link>
                      <Link to="/employee/tasks" className="nav-link" onClick={closeMobileMenu}>
                        My Tasks
                      </Link>
                      <Link to="/employee/salary" className="nav-link" onClick={closeMobileMenu}>
                        Salary & Career
                      </Link>
                      <Link to="/employee/holidays" className="nav-link" onClick={closeMobileMenu}>
                        Holidays
                      </Link>
                      <Link to="/employee/leaves" className="nav-link" onClick={closeMobileMenu}>
                        Leaves
                      </Link>
                      <Link to="/employee/work-updates" className="nav-link" onClick={closeMobileMenu}>
                        Work Updates
                      </Link>
                    </>
                  )}
                </div>

                <div className="nav-right">
                  <Link to="/me/change-password" className="nav-link mobile-only-link" onClick={closeMobileMenu}>
                    Change Password
                  </Link>
                  <button
                    onClick={(e) => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="btn-logout mobile-only-link"
                  >
                    Logout
                  </button>

                  <div className="user-dropdown-container" ref={dropdownRef}>
                    <span 
                      className="nav-user" 
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      {displayPhoto && (
                        <img 
                          src={displayPhoto} 
                          alt="Profile" 
                          className={`nav-profile-photo desktop-photo ${user?.role === 'admin' ? 'admin-logo' : ''}`} 
                        />
                      )}
                      Welcome, {getShortName(user.name)}
                      <span style={{ fontSize: '0.8rem', marginLeft: '4px' }}>▼</span>
                    </span>

                    {isDropdownOpen && (
                      <div className="desktop-dropdown-menu">
                        <Link 
                          to="/me/change-password" 
                          className="dropdown-item" 
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Change Password
                        </Link>
                        <button
                          className="dropdown-item dropdown-logout"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            handleLogout();
                          }}
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="notification-container" ref={desktopNotificationRef}>
                    <button 
                      className="nav-notification-btn desktop-notification"
                      onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    >
                      <Bell size={20} />
                      {notifications.length > 0 && <span className="nav-notification-badge">{notifications.length}</span>}
                    </button>
                    {isNotificationOpen && (
                      <div className="notification-dropdown-menu">
                        <div className="notification-header">
                          <h4>Notifications</h4>
                        </div>
                        <div className="notification-list">
                          {notifications.length === 0 ? (
                            <p className="no-notifications">No new notifications</p>
                          ) : (
                            notifications.map(notif => (
                              <Link to={notif.link} key={notif.id} className="notification-item" onClick={() => setIsNotificationOpen(false)}>
                                {notif.requestType ? (
                                  <>
                                    <p className="notification-title">{notif.requestType}</p>
                                    <p className="notification-name" style={{ margin: '4px 0', fontSize: '14px', fontWeight: 'bold', background: 'linear-gradient(90deg, #003366, #00BFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>{notif.name}</p>
                                    <p className="notification-action" style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>{notif.action}</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="notification-title">{notif.title}</p>
                                    <p className="notification-message">{notif.message}</p>
                                  </>
                                )}
                                <span className="notification-time">{formatDateTime(notif.date)}</span>
                              </Link>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </nav>
      <main className="main-content">{children}</main>
    </div>
  );
};

export default Layout;
